import { useEffect, useId, useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { Button, Card } from '@splitfiesta/ui';

import { Portal } from '@/components/ui/Portal';

export interface ClaimConfirmDialogProps {
  open: boolean;
  /** The saved spot's name. */
  name: string;
  groupName: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The one question before a saved spot becomes the viewer's: the merge runs
 * at once and cannot be undone, so nothing is sent before Yes.
 */
export function ClaimConfirmDialog({
  open,
  name,
  groupName,
  busy = false,
  onConfirm,
  onCancel,
}: ClaimConfirmDialogProps) {
  const { t } = useTranslation();
  useNamespace('groups');

  const titleId = useId();
  const bodyId = useId();
  // A callback ref, not useRef: the Portal mounts its children a tick after
  // the dialog opens, and focus has to wait for the button to exist.
  const [cancelButton, setCancelButton] = useState<HTMLButtonElement | null>(
    null
  );

  useEffect(() => {
    if (!open) return;
    const invoker =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    return () => invoker?.focus();
  }, [open]);

  useEffect(() => {
    if (open) cancelButton?.focus();
  }, [open, cancelButton]);

  // The claim request cannot be called back once sent, so while it is in
  // flight no way out may pretend otherwise.
  useEffect(() => {
    if (!open || busy) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black/50"
          onClick={busy ? undefined : onCancel}
          data-testid="claim-confirm-backdrop"
        />
        <Card
          className="relative w-full max-w-md max-h-full overflow-y-auto"
          padding="none"
          noHoverEffect
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={bodyId}
            className="p-6"
            data-testid="claim-confirm-dialog"
          >
            <h2
              id={titleId}
              className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              {t('groups:placeholderMembers.claimConfirmTitle', { name })}
            </h2>
            <p
              id={bodyId}
              className="mt-2 text-sm text-gray-600 dark:text-gray-300"
            >
              {t('groups:placeholderMembers.claimConfirmBody', {
                name,
                group: groupName,
              })}
            </p>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                ref={setCancelButton}
                variant="secondary"
                onClick={onCancel}
                disabled={busy}
                data-testid="claim-confirm-cancel"
              >
                {t('groups:placeholderMembers.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={onConfirm}
                disabled={busy}
                loading={busy}
                data-testid="claim-confirm-yes"
              >
                {t('groups:placeholderMembers.claimConfirmYes', { name })}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </Portal>
  );
}

export default ClaimConfirmDialog;
